import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'
import { makePaginationParams } from 'sidecar-utilities'

import { initialState, types } from './index'
import { getCurrentPage } from 'utils/interface'
import { mergeArraysRemovingDuplicates } from '../utils/mergeArraysRemovingDuplicates'

// This allows for scrolling through doctor services/costs
export const fetchDoctorCosts = npi => ({
  [RSAA]: {
    endpoint: ({ Doctor }) => {
      const page = getCurrentPage(Doctor.costs.page)
      const size = Doctor.costs.size
      const memberUuid = webStorage.getItem('sub')
      return getApiPath(
        'doc',
        `rates${makeQueryParams({ page, size, npi, memberUuid })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_DOCTOR_COSTS_REQUEST,
      types.FETCH_DOCTOR_COSTS_SUCCESS,
      types.FETCH_DOCTOR_COSTS_ERROR,
    ],
    cache: true,
  },
})

// This is for searching for specific services/costs
export const fetchDoctorCostsByQuery = (npi, query) => {
  return {
    [RSAA]: {
      endpoint: ({ Doctor }) => {
        const size = Doctor.costs.size
        const memberUuid = webStorage.getItem('sub')
        return getApiPath(
          'doc',
          `rates${makeQueryParams({
            page: 0,
            size,
            npi,
            memberUuid,
            query,
          })}`,
          'v2',
        )
      },
      method: 'GET',
      types: [
        types.FETCH_DOCTOR_COSTS_BY_QUERY_REQUEST,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_SUCCESS,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_ERROR,
      ],
      cache: true,
    },
  }
}

export const fetchDoctorCostsByPracticeLocation = (
  { locationUuid, specialties, query },
  paginationParams = {},
) => {
  return {
    [RSAA]: {
      endpoint: () => {
        const memberUuid = webStorage.getItem('sub')
        return getApiPath(
          'doc',
          `doctors/${locationUuid}/location-doctors/rates${makeQueryParams({
            specialties: specialties || [],
            memberUuid,
            query,
            ...makePaginationParams(paginationParams),
          })}`,
          'v2',
        )
      },
      method: 'GET',
      types: [
        types.FETCH_DOCTOR_COSTS_BY_QUERY_REQUEST,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_SUCCESS,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_ERROR,
      ],
      cache: true,
    },
  }
}

export const fetchPracticeLocationCosts = (
  locationId,
  query,
  paginationParams = {},
) => {
  return {
    [RSAA]: {
      endpoint: () =>
        getApiPath(
          'doc',
          `doctors/${locationId}/location/rates${makeQueryParams({
            query,
            ...makePaginationParams(paginationParams),
          })}`,
          'v2',
        ),
      method: 'GET',
      types: [
        types.FETCH_DOCTOR_COSTS_BY_QUERY_REQUEST,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_SUCCESS,
        types.FETCH_DOCTOR_COSTS_BY_QUERY_ERROR,
      ],
      cache: true,
    },
  }
}

export const flushDoctorCosts = () => ({
  type: types.FLUSH_DOCTOR_COSTS,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FLUSH_DOCTOR_COSTS:
      return {
        ...state,
        costs: initialState.costs,
      }

    case types.FETCH_DOCTOR_COSTS_REQUEST:
    case types.FETCH_DOCTOR_COSTS_BY_QUERY_REQUEST:
      return {
        ...state,
        costs: {
          ...state.costs,
          busy: true,
        },
      }

    case types.FETCH_DOCTOR_COSTS_SUCCESS: {
      const { content, page, last } = payload
      const isLastPageReached = last || content.length === 0

      // Because costs are simple lists, we have to approach them a little
      // differently than if they were an ordered map. state.mergeWith works
      // differently than you'd think. we need to operate on the lists
      // (or arrays if we want to work in JS) to concatenate them
      const currentCosts = state.costs.entities || []
      return {
        ...state,
        costs: {
          busy: false,
          entities: [...currentCosts, ...content],
          page,
          isLastPageReached,
        },
      }
    }
    case types.FETCH_DOCTOR_COSTS_ERROR:
    case types.FETCH_DOCTOR_COSTS_BY_QUERY_ERROR:
      return {
        ...state,
        costs: {
          ...state.costs,
          error: payload?.response?.message,
          busy: false,
        },
      }

    case types.FETCH_DOCTOR_COSTS_BY_QUERY_SUCCESS: {
      const { content, page, last } = payload
      const isLastPageReached = last || content.length === 0
      const currentCosts = state.costs.entities || []
      return {
        ...state,
        costs: {
          ...state.costs,
          busy: false,
          entities: mergeArraysRemovingDuplicates({
            arrayOne: currentCosts,
            arrayTwo: content,
            predicate: (a, b) => a.uuid === b.uuid,
          }),
          page,
          isLastPageReached,
        },
      }
    }
    // ----------

    default:
      return state
  }
}
